import type { ReactNode } from "react";
import { Label } from "@/components/ui/label";
import { useErrorText } from "@/lib/errors";

interface FieldProps {
  id: string;
  label: string;
  /** Error code (from the shared Zod schemas or the API); translated via `errors:<code>`. */
  error?: string;
  hint?: ReactNode;
  children: ReactNode;
}

/** Label + control + translated validation message, wired up for screen readers. */
export function Field({ id, label, error, hint, children }: FieldProps) {
  const errorText = useErrorText();
  const message = error ? errorText(error, "invalid") : undefined;
  return (
    <div className="grid min-w-0 content-start gap-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint && !message ? <p className="text-muted-foreground text-sm">{hint}</p> : null}
      {message ? (
        <p id={`${id}-error`} role="alert" className="text-destructive text-sm font-medium">
          {message}
        </p>
      ) : null}
    </div>
  );
}

/** Props for an input inside {@link Field} with an optional error. */
export function fieldA11y(id: string, error?: string) {
  return {
    id,
    "aria-invalid": error ? true : undefined,
    "aria-describedby": error ? `${id}-error` : undefined,
  } as const;
}
